(function () {
  var MAX_ADP_ITEMS = 12;
  var MAX_PRODUCT_JSON_PAGES = 4;
  var PRODUCT_JSON_LIMIT = 250;
  var STOREFRONT_API_VERSION = "2026-04";
  var PRODUCT_CARD_SECTION_ID = "adp-product-card";
  var PRODUCT_LIST_SELECTOR = "[data-adp-product-list]";
  var PRODUCT_CARD_SELECTOR = "product-card[data-adp-spu]";
  var SPU_MAP_SELECTOR = "[data-adp-spu-map]";
  var initializedLists = new WeakMap();
  var renderedResults = new WeakMap();
  var productIndexPromise = null;
  var storefrontProductIndexPromises = new Map();
  var initTimer = null;
  var pendingFallbackTimer = null;

  function completeAdpPinning() {
    document.documentElement.classList.remove("adp-pinning-pending");

    if (pendingFallbackTimer) {
      window.clearTimeout(pendingFallbackTimer);
      pendingFallbackTimer = null;
    }
  }

  pendingFallbackTimer = window.setTimeout(completeAdpPinning, 3500);

  function getAdpRaw() {
    return new URLSearchParams(window.location.search).get("adp") || "";
  }

  function normalizeSpu(value) {
    return String(value || "").trim().toLowerCase();
  }

  function normalizeHandle(value) {
    return String(value || "").trim();
  }

  function getAdpItems() {
    var seen = new Set();

    return getAdpRaw().split(",").map(function (item) {
      return item.trim();
    }).filter(function (item) {
      var normalized = normalizeSpu(item);

      if (!normalized || seen.has(normalized)) {
        return false;
      }

      seen.add(normalized);
      return true;
    }).slice(0, MAX_ADP_ITEMS);
  }

  function getCardSpus(card) {
    return String(card.dataset.adpSpu || "").split(",").map(normalizeSpu).filter(Boolean);
  }

  function getProductKey(card) {
    return card.dataset.productId || card.dataset.productHandle || card.getAttribute("handle") || card.outerHTML;
  }

  function recordFromCard(card) {
    return {
      key: getProductKey(card),
      handle: card.dataset.productHandle || card.getAttribute("handle") || "",
      spus: getCardSpus(card),
      html: card.outerHTML
    };
  }

  function recordsFromList(productList) {
    return Array.from(productList.querySelectorAll(PRODUCT_CARD_SELECTOR)).map(recordFromCard);
  }

  function cacheBaseKey(sectionId) {
    var url = new URL(window.location.href);
    url.searchParams.delete("page");
    url.searchParams.delete("section_id");

    return "adp-product-pinning:" + sectionId + ":" + url.pathname + "?" + url.searchParams.toString();
  }

  function productCardCacheKey(handle) {
    var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/";

    return "adp-product-card:" + root + handle;
  }

  function productIndexCacheKey() {
    var url = new URL(window.location.href);
    url.search = "";
    url.hash = "";

    return "adp-product-index:" + url.pathname;
  }

  function storefrontProductIndexCacheKey(collectionHandle) {
    return "adp-storefront-product-index:" + normalizeHandle(collectionHandle || "all");
  }

  function readCachedProductCard(handle) {
    try {
      return sessionStorage.getItem(productCardCacheKey(handle));
    } catch (error) {
      return null;
    }
  }

  function writeCachedProductCard(handle, html) {
    try {
      sessionStorage.setItem(productCardCacheKey(handle), html);
    } catch (error) {
    }
  }

  function readCachedProductIndex() {
    try {
      var cached = sessionStorage.getItem(productIndexCacheKey());

      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      return null;
    }
  }

  function writeCachedProductIndex(index) {
    try {
      sessionStorage.setItem(productIndexCacheKey(), JSON.stringify(index));
    } catch (error) {
    }
  }

  function readCachedStorefrontProductIndex(collectionHandle) {
    try {
      var cached = sessionStorage.getItem(storefrontProductIndexCacheKey(collectionHandle));

      return cached ? JSON.parse(cached) : {};
    } catch (error) {
      return {};
    }
  }

  function writeCachedStorefrontProductIndex(collectionHandle, index) {
    try {
      sessionStorage.setItem(storefrontProductIndexCacheKey(collectionHandle), JSON.stringify(index));
    } catch (error) {
    }
  }

  function recordFromHtml(html) {
    var doc = new DOMParser().parseFromString(html, "text/html");
    var card = doc.querySelector(PRODUCT_CARD_SELECTOR);

    return card ? recordFromCard(card) : null;
  }

  function getRouteRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }

    return "/";
  }

  function getCollectionProductsJsonUrl(page) {
    var pathname = window.location.pathname.replace(/\/$/, "");
    var url = new URL(pathname + "/products.json", window.location.origin);

    url.searchParams.set("limit", PRODUCT_JSON_LIMIT);
    url.searchParams.set("page", page);

    return url;
  }

  function getStorefrontGraphqlUrl() {
    return new URL("/api/" + STOREFRONT_API_VERSION + "/graphql.json", window.location.origin);
  }

  function getStorefrontSearchQuery(collectionHandle, spu) {
    return "collection:" + normalizeHandle(collectionHandle || "all") + " metafield:custom.spu:" + String(spu || "").trim();
  }

  function buildStorefrontProductIndexPayload(collectionHandle, missingSpus) {
    var variableDefinitions = [];
    var fields = [];
    var variables = {};

    missingSpus.forEach(function (item, index) {
      var variableName = "spuQuery" + index;

      variableDefinitions.push("$" + variableName + ": String!");
      fields.push("p" + index + ": products(first: 1, query: $" + variableName + ") { nodes { handle } }");
      variables[variableName] = getStorefrontSearchQuery(collectionHandle, item.spu);
    });

    return {
      query: "query AdpStorefrontProductIndex(" + variableDefinitions.join(", ") + ") { " + fields.join(" ") + " }",
      variables: variables
    };
  }

  async function fetchStorefrontProductIndex(collectionHandle, spus) {
    var cachedIndex = readCachedStorefrontProductIndex(collectionHandle);
    var index = {};
    var missingSpus = [];
    var promiseKey;

    spus.forEach(function (spu) {
      var normalizedSpu = normalizeSpu(spu);

      if (!normalizedSpu) {
        return;
      }

      if (cachedIndex[normalizedSpu]) {
        index[normalizedSpu] = cachedIndex[normalizedSpu];
      } else {
        missingSpus.push({
          normalizedSpu: normalizedSpu,
          spu: String(spu || "").trim()
        });
      }
    });

    if (!missingSpus.length) {
      return index;
    }

    promiseKey = normalizeHandle(collectionHandle || "all") + ":" + missingSpus.map(function (item) {
      return item.normalizedSpu;
    }).join(",");

    if (storefrontProductIndexPromises.has(promiseKey)) {
      return storefrontProductIndexPromises.get(promiseKey);
    }

    storefrontProductIndexPromises.set(promiseKey, (async function () {
      try {
        var response = await fetch(getStorefrontGraphqlUrl().toString(), {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json"
          },
          body: JSON.stringify(buildStorefrontProductIndexPayload(collectionHandle, missingSpus))
        });

        if (!response.ok) {
          return index;
        }

        var payload = await response.json();
        var data = payload && payload.data ? payload.data : {};
        var hasNewHandle = false;

        missingSpus.forEach(function (item, itemIndex) {
          var result = data["p" + itemIndex];
          var nodes = result && Array.isArray(result.nodes) ? result.nodes : [];
          var handle = nodes[0] ? normalizeHandle(nodes[0].handle) : "";

          if (handle) {
            index[item.normalizedSpu] = handle;
            cachedIndex[item.normalizedSpu] = handle;
            hasNewHandle = true;
          }
        });

        if (hasNewHandle) {
          writeCachedStorefrontProductIndex(collectionHandle, cachedIndex);
        }
      } catch (error) {
      }

      return index;
    })());

    return storefrontProductIndexPromises.get(promiseKey);
  }

  function addProductToIndex(index, product) {
    var handle = normalizeHandle(product.handle);

    if (!handle) {
      return;
    }

    (product.tags || []).forEach(function (tag) {
      var normalizedTag = String(tag || "").trim();

      if (normalizedTag.toLowerCase().indexOf("spu:") === 0) {
        index[normalizeSpu(normalizedTag.slice(4))] = handle;
      }
    });

    (product.variants || []).forEach(function (variant) {
      var sku = String(variant.sku || "").trim();
      var skuSpu = sku.split("-")[0];

      if (skuSpu) {
        index[normalizeSpu(skuSpu)] = handle;
      }
    });
  }

  async function fetchProductIndex() {
    var cachedIndex = readCachedProductIndex();
    var index = cachedIndex || {};

    if (cachedIndex) {
      return index;
    }

    if (productIndexPromise) {
      return productIndexPromise;
    }

    productIndexPromise = (async function () {
      for (var page = 1; page <= MAX_PRODUCT_JSON_PAGES; page += 1) {
        var response = await fetch(getCollectionProductsJsonUrl(page).toString(), {
          credentials: "same-origin",
          headers: {
            "Accept": "application/json"
          }
        });

        if (!response.ok) {
          break;
        }

        var payload = await response.json();
        var products = Array.isArray(payload.products) ? payload.products : [];

        if (!products.length) {
          break;
        }

        products.forEach(function (product) {
          addProductToIndex(index, product);
        });

        if (products.length < PRODUCT_JSON_LIMIT) {
          break;
        }
      }

      writeCachedProductIndex(index);
      return index;
    })();

    return productIndexPromise;
  }

  async function fetchProductCardByHandle(handle) {
    var cachedHtml = readCachedProductCard(handle);

    if (cachedHtml) {
      return recordFromHtml(cachedHtml);
    }

    var url = new URL(getRouteRoot() + "products/" + encodeURIComponent(handle), window.location.origin);
    url.searchParams.set("section_id", PRODUCT_CARD_SECTION_ID);

    var response = await fetch(url.toString(), {
      credentials: "same-origin",
      headers: {
        "X-Requested-With": "XMLHttpRequest"
      }
    });

    if (!response.ok) {
      throw new Error("Unable to fetch ADP product card " + handle);
    }

    var html = await response.text();
    var record = recordFromHtml(html);

    if (record) {
      writeCachedProductCard(handle, record.html);
    }

    return record;
  }

  function readSpuMap(section) {
    var mapElement = section.querySelector(SPU_MAP_SELECTOR) || document.querySelector(SPU_MAP_SELECTOR);
    var parsedMap;
    var spuMap = new Map();

    if (!mapElement || !mapElement.textContent.trim()) {
      return spuMap;
    }

    try {
      parsedMap = JSON.parse(mapElement.textContent);
    } catch (error) {
      return spuMap;
    }

    Object.keys(parsedMap).forEach(function (spu) {
      var handle = String(parsedMap[spu] || "").trim();

      if (handle) {
        spuMap.set(normalizeSpu(spu), handle);
      }
    });

    return spuMap;
  }

  function getExpectedPinnedHandles(adpItems, spuMap) {
    var seen = new Set();

    return adpItems.map(function (spu) {
      return spuMap.get(normalizeSpu(spu));
    }).filter(function (handle) {
      if (!handle || seen.has(handle)) {
        return false;
      }

      seen.add(handle);
      return true;
    });
  }

  function isListPinned(productList, pinnedHandles) {
    var productCards;

    if (!pinnedHandles.length) {
      return false;
    }

    productCards = Array.from(productList.querySelectorAll("product-card"));

    return pinnedHandles.every(function (handle, index) {
      var card = productCards[index];

      return card && (card.dataset.productHandle || card.getAttribute("handle")) === handle;
    });
  }

  function getPromoRecords(productList) {
    var productPosition = 1;

    return Array.from(productList.children).reduce(function (promos, child) {
      if (child.matches("product-card")) {
        productPosition += 1;
      } else if (child.classList.contains("product-list__promo")) {
        promos.push({
          position: productPosition,
          html: child.outerHTML
        });
      }

      return promos;
    }, []);
  }

  function elementFromHtml(html) {
    var template = document.createElement("template");
    template.innerHTML = html.trim();

    return template.content.firstElementChild;
  }

  function tuneLoadingForVisibleCards(productList) {
    Array.from(productList.querySelectorAll("product-card")).forEach(function (card, index) {
      var primaryImage = card.querySelector(".product-card__image--primary");

      if (!primaryImage) {
        return;
      }

      if (index < 3) {
        primaryImage.removeAttribute("loading");
      } else if (!primaryImage.hasAttribute("loading")) {
        primaryImage.setAttribute("loading", "lazy");
      }
    });
  }

  function renderPage(productList, records) {
    var productsPerPage = parseInt(productList.dataset.adpProductsPerPage || "0", 10);
    var pageRecords = records.slice(0, productsPerPage);
    var promoRecords = getPromoRecords(productList);
    var fragment = document.createDocumentFragment();

    if (!productsPerPage || !pageRecords.length) {
      return;
    }

    pageRecords.forEach(function (record, index) {
      var productPosition = index + 1;

      promoRecords.forEach(function (promo) {
        if (promo.position === productPosition) {
          fragment.appendChild(elementFromHtml(promo.html));
        }
      });

      fragment.appendChild(elementFromHtml(record.html));
    });

    productList.replaceChildren(fragment);
    tuneLoadingForVisibleCards(productList);
  }

  async function collectRecords(productList, adpItems, spuMap) {
    var currentRecords = recordsFromList(productList);
    var currentRecordsByHandle = new Map();
    var currentRecordsBySpu = new Map();
    var usedKeys = new Set();
    var collectionHandle = productList.dataset.adpCollectionHandle || "all";
    var storefrontIndex;
    var pinnedRecords;
    var pinnedHandles;

    currentRecords.forEach(function (record) {
      if (record.handle) {
        currentRecordsByHandle.set(record.handle, record);
      }

      record.spus.forEach(function (spu) {
        if (!currentRecordsBySpu.has(spu)) {
          currentRecordsBySpu.set(spu, record);
        }
      });
    });

    storefrontIndex = await fetchStorefrontProductIndex(collectionHandle, adpItems.filter(function (spu) {
      var normalizedSpu = normalizeSpu(spu);

      return normalizedSpu && !spuMap.get(normalizedSpu) && !currentRecordsBySpu.has(normalizedSpu);
    }));

    pinnedRecords = await Promise.all(adpItems.map(async function (spu) {
      var normalizedSpu = normalizeSpu(spu);
      var handle = spuMap.get(normalizedSpu);

      if (currentRecordsBySpu.has(normalizedSpu)) {
        return currentRecordsBySpu.get(normalizedSpu);
      }

      if (!handle) {
        handle = normalizeHandle(storefrontIndex[normalizedSpu]);
      }

      if (!handle) {
        var productIndex = await fetchProductIndex();
        handle = normalizeHandle(productIndex[normalizedSpu]);
      }

      if (!handle) {
        return null;
      }

      if (currentRecordsByHandle.has(handle)) {
        return currentRecordsByHandle.get(handle);
      }

      try {
        return await fetchProductCardByHandle(handle);
      } catch (error) {
        return null;
      }
    }));

    pinnedRecords = pinnedRecords.filter(function (record) {
      if (!record || usedKeys.has(record.key)) {
        return false;
      }

      usedKeys.add(record.key);
      return true;
    });

    pinnedHandles = pinnedRecords.map(function (record) {
      return record.handle;
    }).filter(Boolean);

    return {
      records: pinnedRecords.concat(currentRecords.filter(function (record) {
        if (usedKeys.has(record.key)) {
          return false;
        }

        usedKeys.add(record.key);
        return true;
      })),
      pinnedHandles: pinnedHandles
    };
  }

  function renderResult(productList, result) {
    if (!result || !result.pinnedHandles.length) {
      return;
    }

    renderedResults.set(productList, result);
    renderPage(productList, result.records);
  }

  function restoreRenderedResult(productList, expectedPinnedHandles) {
    var result = renderedResults.get(productList);

    if (!result) {
      return false;
    }

    if (isListPinned(productList, result.pinnedHandles)) {
      return true;
    }

    if (!expectedPinnedHandles.length) {
      return false;
    }

    renderPage(productList, result.records);
    return true;
  }

  function preserveAdpOnPagination(section, adpRaw) {
    section.querySelectorAll(".collection__pagination a[href]").forEach(function (link) {
      var url = new URL(link.href, window.location.origin);
      url.searchParams.set("adp", adpRaw);
      link.href = url.pathname + url.search + url.hash;
    });
  }

  function preserveAdpOnSortLinks(section, adpRaw) {
    section.querySelectorAll("facet-sort-by").forEach(function (sortBy) {
      if (sortBy.dataset.adpSortPreserved === "true") {
        return;
      }

      sortBy.dataset.adpSortPreserved = "true";
      sortBy.addEventListener("listbox:change", function () {
        var url = new URL(window.location.href);
        url.searchParams.set("adp", adpRaw);
        history.replaceState({}, "", url.toString());
      }, true);
    });
  }

  async function initProductList(productList, adpItems, adpRaw) {
    var initKey = cacheBaseKey(productList.dataset.adpSectionId || "") + ":page:" + (productList.dataset.adpCurrentPage || "1");
    var section = productList.closest(".shopify-section") || document;
    var currentPage = parseInt(productList.dataset.adpCurrentPage || "1", 10);
    var spuMap = readSpuMap(section);
    var expectedPinnedHandles = getExpectedPinnedHandles(adpItems, spuMap);

    if (initializedLists.get(productList) === initKey) {
      if (isListPinned(productList, (renderedResults.get(productList) || {}).pinnedHandles || [])) {
        return;
      }

      if (restoreRenderedResult(productList, expectedPinnedHandles)) {
        return;
      }

      return;
    }

    initializedLists.set(productList, initKey);
    preserveAdpOnPagination(section, adpRaw);

    if (currentPage !== 1) {
      return;
    }

    preserveAdpOnSortLinks(section, adpRaw);

    try {
      var result = await collectRecords(productList, adpItems, spuMap);

      renderResult(productList, result);
    } catch (error) {
    }

    preserveAdpOnPagination(section, adpRaw);
    preserveAdpOnSortLinks(section, adpRaw);
  }

  function initAdpProductPinning() {
    var adpRaw = getAdpRaw();
    var adpItems = getAdpItems();
    var productLists;

    if (!adpRaw || !adpItems.length) {
      completeAdpPinning();
      return;
    }

    productLists = Array.from(document.querySelectorAll(PRODUCT_LIST_SELECTOR));

    if (!productLists.length) {
      completeAdpPinning();
      return;
    }

    Promise.all(productLists.map(function (productList) {
      return initProductList(productList, adpItems, adpRaw);
    })).then(completeAdpPinning).catch(completeAdpPinning);
  }

  function scheduleInit() {
    window.clearTimeout(initTimer);
    initTimer = window.setTimeout(initAdpProductPinning, 50);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAdpProductPinning);
  } else {
    initAdpProductPinning();
  }

  new MutationObserver(scheduleInit).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
